import { useState } from 'react';
import { useReportBuilderStore, getAllowedAggregations, getDefaultAggregation } from '../../stores/reportBuilderStore';
import type { AnnotatedColumn } from '../../stores/reportBuilderStore';
import { CaretDown, CaretRight, MagnifyingGlass, X } from '@phosphor-icons/react';
import type { AggregationType } from '../../types/reportBuilder';

const DATA_TYPE_BADGES: Record<string, string> = {
  string: 'bg-purple-100 text-purple-700',
  integer: 'bg-orange-100 text-orange-700',
  decimal: 'bg-orange-100 text-orange-700',
  boolean: 'bg-yellow-100 text-yellow-700',
  date: 'bg-teal-100 text-teal-700',
  timestamp: 'bg-teal-100 text-teal-700',
  uuid: 'bg-gray-100 text-gray-700',
};

function groupByModel(cols: AnnotatedColumn[]): Record<string, AnnotatedColumn[]> {
  const groups: Record<string, AnnotatedColumn[]> = {};
  for (const col of cols) {
    const key = col.sourceModelName;
    if (!groups[key]) groups[key] = [];
    groups[key].push(col);
  }
  return groups;
}

function groupStandardAndCustom(cols: AnnotatedColumn[]): { standard: AnnotatedColumn[]; custom: AnnotatedColumn[] } {
  const standard: AnnotatedColumn[] = [];
  const custom: AnnotatedColumn[] = [];
  for (const col of cols) {
    if (col.is_custom_field) custom.push(col);
    else standard.push(col);
  }
  return { standard, custom };
}

export default function ColumnPicker() {
  const {
    selectedModel, selectedModelKeys, selectedDimensions, selectedMeasures,
    getDimensionColumns, getMeasureColumns, getAllColumns,
    addDimension, removeDimension, setDimensions,
    addMeasure, removeMeasure, updateMeasure,
  } = useReportBuilderStore();

  const [dimOpen, setDimOpen] = useState(true);
  const [measOpen, setMeasOpen] = useState(true);
  const [search, setSearch] = useState('');

  if (!selectedModel) return <div className="text-sm text-gray-400 italic">Select a data model to see columns</div>;

  const dimensionCols = getDimensionColumns();
  const measureCols = getMeasureColumns();
  const allColumns = getAllColumns();
  const hasJoins = selectedModelKeys.length > 1;

  const sortSelectedFirst = (cols: AnnotatedColumn[]) => {
    const selected: AnnotatedColumn[] = [];
    const unselected: AnnotatedColumn[] = [];
    for (const col of cols) {
      const isSelected = col.is_dimension
        ? selectedDimensions.includes(col.refKey)
        : selectedMeasures.some(m => m.column === col.refKey);
      if (isSelected) selected.push(col);
      else unselected.push(col);
    }
    return [...selected, ...unselected];
  };

  const filteredDims = sortSelectedFirst(search ? dimensionCols.filter(c => c.label.toLowerCase().includes(search.toLowerCase())) : dimensionCols);
  const filteredMeas = sortSelectedFirst(search ? measureCols.filter(c => c.label.toLowerCase().includes(search.toLowerCase())) : measureCols);

  const handleSelectDefaults = () => {
    const defaults = selectedModel.column_order.filter(key => {
      const col = selectedModel.columns[key];
      return col?.default_visible && col.is_dimension;
    });
    setDimensions(defaults);
  };

  const toggleDimension = (refKey: string) => {
    selectedDimensions.includes(refKey) ? removeDimension(refKey) : addDimension(refKey);
  };

  const handleMeasureToggle = (col: AnnotatedColumn) => {
    const existing = selectedMeasures.find(m => m.column === col.refKey);
    if (existing) {
      removeMeasure(existing.alias);
    } else {
      const defaultAgg = getDefaultAggregation(col);
      const aliasKey = col.refKey.replace(/\./g, '_');
      addMeasure({ column: col.refKey, aggregation: defaultAgg, alias: `${defaultAgg}_${aliasKey}` });
    }
  };

  const handleAggChange = (col: AnnotatedColumn, agg: AggregationType) => {
    const existing = selectedMeasures.find(m => m.column === col.refKey);
    if (existing) {
      const aliasKey = col.refKey.replace(/\./g, '_');
      updateMeasure(existing.alias, { column: col.refKey, aggregation: agg, alias: `${agg}_${aliasKey}` });
    }
  };

  const renderDimensionList = (cols: AnnotatedColumn[]) => (
    <div className="space-y-0.5">
      {cols.map(col => (
        <label key={col.refKey} className="flex items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-gray-50 cursor-pointer">
          <input type="checkbox" checked={selectedDimensions.includes(col.refKey)} onChange={() => toggleDimension(col.refKey)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
          <span className="flex-1 truncate">{col.label}</span>
          {col.is_custom_field && <span className="rounded px-1 py-0.5 text-[10px] font-medium bg-indigo-100 text-indigo-700">Custom</span>}
          <span className={`rounded px-1 py-0.5 text-[10px] font-medium ${DATA_TYPE_BADGES[col.data_type] || 'bg-gray-100 text-gray-600'}`}>{col.data_type}</span>
        </label>
      ))}
    </div>
  );

  const renderMeasureList = (cols: AnnotatedColumn[]) => (
    <div className="space-y-0.5">
      {cols.map(col => {
        const active = selectedMeasures.find(m => m.column === col.refKey);
        const aggTypes = getAllowedAggregations(col);
        return (
          <div key={col.refKey} className="flex items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-gray-50">
            <input type="checkbox" checked={!!active} onChange={() => handleMeasureToggle(col)}
              className="rounded border-gray-300 text-green-600 focus:ring-green-500" />
            <span className="flex-1 truncate">{col.label}</span>
            {active && aggTypes.length > 1 && (
              <select value={active.aggregation} onChange={(e) => handleAggChange(col, e.target.value as AggregationType)}
                className="rounded border border-gray-300 px-1 py-0.5 text-xs">
                {aggTypes.map(agg => <option key={agg} value={agg}>{agg}</option>)}
              </select>
            )}
          </div>
        );
      })}
    </div>
  );

  const dimGroups = hasJoins ? groupByModel(filteredDims) : null;
  const measGroups = hasJoins ? groupByModel(filteredMeas) : null;
  const dimSplit = !hasJoins ? groupStandardAndCustom(filteredDims) : null;

  return (
    <div className="space-y-3">
      <div className="relative">
        <MagnifyingGlass className="absolute left-2 top-2 h-4 w-4 text-gray-400" />
        <input type="text" placeholder="Search columns..." value={search} onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-md border border-gray-300 py-1.5 pl-8 pr-8 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
        {search && <button onClick={() => setSearch('')} className="absolute right-2 top-2"><X className="h-4 w-4 text-gray-400 hover:text-gray-600" /></button>}
      </div>

      <button onClick={handleSelectDefaults} className="text-xs text-blue-600 hover:text-blue-800">Select defaults</button>

      {(selectedDimensions.length > 0 || selectedMeasures.length > 0) && (
        <div className="flex flex-wrap gap-1">
          {selectedDimensions.map(refKey => {
            const col = allColumns[refKey];
            return (
              <span key={refKey} className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700">
                {col?.label || refKey}
                <button onClick={() => removeDimension(refKey)}><X className="h-3 w-3" /></button>
              </span>
            );
          })}
          {selectedMeasures.map(m => (
            <span key={m.alias} className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
              {m.alias}
              <button onClick={() => removeMeasure(m.alias)}><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}

      <div>
        <button onClick={() => setDimOpen(!dimOpen)} className="flex w-full items-center gap-1 text-sm font-medium text-gray-700">
          {dimOpen ? <CaretDown className="h-4 w-4" /> : <CaretRight className="h-4 w-4" />}
          Dimensions ({filteredDims.length})
        </button>
        {dimOpen && (
          <div className="mt-1 max-h-48 overflow-y-auto pl-5">
            {dimGroups ? Object.entries(dimGroups).map(([modelName, cols]) => (
              <div key={modelName} className="mb-2">
                <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">{modelName}</p>
                {renderDimensionList(cols)}
              </div>
            )) : dimSplit && dimSplit.custom.length > 0 ? (
              <>
                {renderDimensionList(dimSplit.standard)}
                <div className="mt-2 border-t border-gray-100 pt-2">
                  <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-400">Custom Fields</p>
                  {renderDimensionList(dimSplit.custom)}
                </div>
              </>
            ) : renderDimensionList(filteredDims)}
          </div>
        )}
      </div>

      <div>
        <button onClick={() => setMeasOpen(!measOpen)} className="flex w-full items-center gap-1 text-sm font-medium text-gray-700">
          {measOpen ? <CaretDown className="h-4 w-4" /> : <CaretRight className="h-4 w-4" />}
          Measures ({filteredMeas.length})
        </button>
        {measOpen && (
          <div className="mt-1 max-h-48 overflow-y-auto pl-5">
            {measGroups ? Object.entries(measGroups).map(([modelName, cols]) => (
              <div key={modelName} className="mb-2">
                <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">{modelName}</p>
                {renderMeasureList(cols)}
              </div>
            )) : renderMeasureList(filteredMeas)}
          </div>
        )}
      </div>
    </div>
  );
}
